import React, { useEffect, useState } from 'react';
import { db } from '../../firebase-config';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';

const Films = () => {
  const [films, setFilms] = useState([]);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [type, setType] = useState('large'); // 'large' or 'reel'
  const [thumbnail, setThumbnail] = useState('');
  const [showOnLanding, setShowOnLanding] = useState(false);
  const [fetchingThumb, setFetchingThumb] = useState(false);
  
  // State for editing items
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const loadFilms = async () => {
    try {
      const q = query(collection(db, 'films'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setFilms(snap.docs.map(d => ({ ...d.data(), firestoreId: d.id })));
    } catch (err) {
      console.error('Error loading films', err);
    }
  };

  useEffect(() => { loadFilms(); }, []);

  const handleUrlChange = (newUrl) => {
    setUrl(newUrl);
    if (newUrl.length > 10) {
      detectTypeAndFetchThumb(newUrl);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!title.trim()) return alert('Unesite naslov filma');
    if (!url.trim()) return alert('Unesite URL filma');
    
    try {
      const finalUrl = url.trim();
      await addDoc(collection(db, 'films'), { 
        title: title.trim(), 
        url: finalUrl, 
        type, 
        thumbnail: thumbnail.trim() || '/img/photos/default.jpg', 
        showOnLanding,
        createdAt: new Date() 
      });
      setTitle(''); setUrl(''); setType('large'); setThumbnail(''); setShowOnLanding(false);
      loadFilms();
    } catch (err) { console.error(err); alert('Greška pri dodavanju'); }
  };

  const startEditing = (film) => {
    setEditingId(film.firestoreId);
    setEditTitle(film.title);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditTitle('');
  };

  const saveEdit = async (id) => {
    if (!editTitle.trim()) return alert('Naslov ne može biti prazan');
    setSavingEdit(true);
    try {
      await updateDoc(doc(db, 'films', id), {
        title: editTitle.trim()
      });
      setEditingId(null);
      loadFilms();
    } catch (err) {
      console.error(err);
      alert('Greška pri spašavanju');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleLanding = async (film) => {
    try {
      await updateDoc(doc(db, 'films', film.firestoreId), {
        showOnLanding: !film.showOnLanding
      });
      loadFilms();
    } catch (err) {
      console.error(err);
    }
  };

  const getYouTubeId = (u) => {
    try {
      const urlObj = new URL(u);
      if (urlObj.hostname.includes('youtube.com/shorts/')) {
        const id = urlObj.pathname.split('shorts/')[1].split(/[?&]/)[0];
        return { id, isShort: true };
      }
      if (urlObj.hostname.includes('youtu.be')) return { id: urlObj.pathname.slice(1), isShort: false };
      if (urlObj.hostname.includes('youtube.com')) {
        const p = urlObj.searchParams.get('v');
        if (p) return { id: p, isShort: false };
        // shorts path check in pathname too
        if (urlObj.pathname.includes('/shorts/')) {
          const id = urlObj.pathname.split('/shorts/')[1].split(/[?&]/)[0];
          return { id, isShort: true };
        }
        const parts = urlObj.pathname.split('/').filter(Boolean);
        if (parts.includes('embed')) return { id: parts[parts.indexOf('embed')+1] || null, isShort: false };
      }
    } catch (e) {
      return null;
    }
    return null;
  };

  const detectTypeAndFetchThumb = async (inputUrl) => {
    const u = (inputUrl || '').trim();
    if (!u) return;
    setFetchingThumb(true);

    // Auto-detect type
    let detectedType = 'large';
    const lowerUrl = u.toLowerCase();
    
    if (
      lowerUrl.includes('instagram.com/reels/') || 
      lowerUrl.includes('instagram.com/reel/') || 
      lowerUrl.includes('instagram.com/p/') ||
      lowerUrl.includes('tiktok.com') ||
      lowerUrl.includes('youtube.com/shorts/')
    ) {
      detectedType = 'reel';
    }
    setType(detectedType);

    try {
      // YouTube quick path
      const ytData = getYouTubeId(u);
      if (ytData && ytData.id) {
        const ytId = ytData.id;
        if (ytData.isShort) setType('reel');
        
        // try maxres then fallback to hq
        const maxres = `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`;
        const hq = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;

        // Fetch YouTube Metadata via oEmbed
        try {
          const oEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${ytId}&format=json`;
          const response = await fetch(oEmbedUrl);
          if (response.ok) {
            const data = await response.json();
            if (data.title) setTitle(data.title);
          }
        } catch (e) {
          console.log('Failed to fetch YouTube title');
        }

        // quick check if maxres exists
        try {
          const r = await fetch(maxres, { method: 'HEAD' });
          if (r.ok) {
            setThumbnail(maxres);
            return;
          }
        } catch (e) {}
        setThumbnail(hq);
        return;
      }

      // Vimeo oEmbed (works without auth)
      try {
        const v = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(u)}`);
        if (v.ok) {
          const j = await v.json();
          if (j.thumbnail_url) setThumbnail(j.thumbnail_url);
          if (j.title) setTitle(j.title);
          return;
        }
      } catch (e) {}

      // TikTok oEmbed
      if (lowerUrl.includes('tiktok.com')) {
        try {
          const t = await fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(u)}`);
          if (t.ok) {
            const j = await t.json();
            if (j.thumbnail_url) setThumbnail(j.thumbnail_url);
            if (j.title) setTitle(j.title);
          }
        } catch (e) {}
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setFetchingThumb(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Obriši film?')) return;
    try {
      await deleteDoc(doc(db, 'films', id));
      loadFilms();
    } catch (err) { console.error(err); alert('Greška pri brisanju'); }
  };

  return (
    <div className="space-y-8">
      {/* ADD SECTION */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gray-50/50 px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-800">Video Manager</h2>
          <p className="text-xs text-gray-500">Dodajte nove filmove, shorts-e ili reels-e</p>
        </div>

        <form onSubmit={handleAdd} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 ml-1">Naslov Materijala</label>
              <input 
                value={title} 
                onChange={e => setTitle(e.target.value)} 
                placeholder="npr. Vjenčanje Lamija & Armin" 
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-black outline-none transition-all" 
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 ml-1">Video Link (YouTube, Instagram, TikTok)</label>
              <div className="relative">
                <input 
                  value={url} 
                  onChange={e => handleUrlChange(e.target.value)} 
                  placeholder="Zalijepite link ovdje..." 
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-black outline-none transition-all pr-24" 
                />
                <button 
                  type="button" 
                  onClick={() => detectTypeAndFetchThumb(url)} 
                  className="absolute right-2 top-2 bottom-2 px-3 bg-white border border-gray-200 rounded-lg text-[10px] font-bold uppercase hover:bg-gray-50 transition-colors"
                >
                  {fetchingThumb ? '...' : 'Re-fetch'}
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-start gap-8">
            <div className="space-y-2 min-w-[240px]">
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 ml-1">Tip Sadržaja</label>
              <select 
                value={type} 
                onChange={e => setType(e.target.value)} 
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-black outline-none appearance-none cursor-pointer"
              >
                <option value="large">Glavni Film (Horizontalni 16:9)</option>
                <option value="reel">Reels / Shorts / TikTok (Vertikalni 9:16)</option>
              </select>
            </div>

            <div className="flex items-center gap-3 pt-6">
              <input 
                type="checkbox" 
                id="showLanding" 
                checked={showOnLanding} 
                onChange={e => setShowOnLanding(e.target.checked)}
                className="w-5 h-5 accent-black rounded cursor-pointer" 
              />
              <label htmlFor="showLanding" className="text-sm font-medium text-gray-700 cursor-pointer">
                Prikaži na naslovnoj stranici
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 ml-1">Thumbnail Preview</label>
            <div className="flex gap-4 items-center p-3 bg-gray-50 rounded-xl border border-gray-100">
              <div className={`shrink-0 overflow-hidden rounded-lg bg-gray-200 border border-gray-200 ${type === 'reel' ? 'w-16 h-28' : 'w-32 h-20'}`}>
                {thumbnail ? (
                  <img src={thumbnail} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input 
                  value={thumbnail} 
                  onChange={e => setThumbnail(e.target.value)} 
                  placeholder="Ručni unos thumbnail linka..." 
                  className="w-full bg-transparent text-xs border-b border-gray-200 py-1 outline-none focus:border-black transition-colors" 
                />
                <p className="mt-1 text-[10px] text-gray-400 italic">Thumbnail se generiše automatski.</p>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-50 flex justify-end">
            <button className="px-8 py-3 bg-black text-white rounded-xl text-sm font-bold hover:shadow-lg hover:bg-gray-900 transition-all flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
              Spremi Video
            </button>
          </div>
        </form>
      </div>

      {/* LIST SECTION */}
      <div className="grid grid-cols-1 gap-12">
        {/* LARGE FILMS */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400">Glavni Filmovi</h3>
            <div className="h-px bg-gray-100 flex-1"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {films.filter(f => f.type === 'large').map(f => (
              <div key={f.firestoreId} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm flex flex-col">
                <div className="aspect-video bg-gray-100 relative group">
                  <img src={f.thumbnail} alt={f.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <a href={f.url} target="_blank" rel="noreferrer" className="bg-white text-black px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest">
                      Pogledaj Link
                    </a>
                  </div>
                  {f.showOnLanding && (
                    <div className="absolute top-3 left-3 bg-yellow-400 text-black text-[9px] font-black uppercase px-2 py-1 rounded shadow-sm tracking-tighter">
                      Prikazano na Naslovnoj
                    </div>
                  )}
                </div>
                
                <div className="p-5 flex-1 flex flex-col gap-4">
                  {editingId === f.firestoreId ? (
                    <div className="space-y-3">
                      <input 
                        value={editTitle} 
                        onChange={e => setEditTitle(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-black"
                        autoFocus 
                      />
                      <div className="flex gap-2">
                        <button 
                          onClick={() => saveEdit(f.firestoreId)}
                          disabled={savingEdit}
                          className="flex-1 bg-green-600 text-white py-2 rounded-lg text-[10px] font-bold uppercase disabled:opacity-50"
                        >
                          {savingEdit ? '...' : 'Spremi'}
                        </button>
                        <button 
                          onClick={cancelEditing}
                          className="flex-1 bg-gray-100 text-gray-600 py-2 rounded-lg text-[10px] font-bold uppercase"
                        >
                          Nazad
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="font-bold text-gray-900 line-clamp-2">{f.title}</div>
                      <div className="mt-auto pt-4 border-t border-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => startEditing(f)}
                            className="p-2 text-gray-400 hover:text-black transition-colors"
                            title="Uredi naslov"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                          </button>
                          <button 
                            onClick={() => toggleLanding(f)}
                            className={`p-2 transition-colors ${f.showOnLanding ? 'text-yellow-600' : 'text-gray-300 hover:text-yellow-500'}`}
                            title={f.showOnLanding ? 'Ukloni sa naslovne' : 'Prikaži na naslovnoj'}
                          >
                            <svg className="w-4 h-4" fill={f.showOnLanding ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.382-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                          </button>
                        </div>
                        <button onClick={() => handleDelete(f.firestoreId)} className="p-2 text-gray-300 hover:text-red-600 transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* REELS */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400">Reels / Shorts / TikTok</h3>
            <div className="h-px bg-gray-100 flex-1"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {films.filter(f => f.type === 'reel').map(f => (
              <div key={f.firestoreId} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm flex flex-col">
                <div className="aspect-[9/16] bg-gray-100 relative group">
                  <img src={f.thumbnail} alt={f.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-4">
                    <a href={f.url} target="_blank" rel="noreferrer" className="bg-white text-black px-3 py-1.5 rounded-full text-[9px] font-bold uppercase tracking-wider mb-2">
                      Link
                    </a>
                  </div>
                  {f.showOnLanding && (
                    <div className="absolute top-2 left-2 bg-yellow-400 text-black text-[8px] font-black uppercase px-2 py-1 rounded shadow-sm tracking-tighter">
                      Na Naslovnoj
                    </div>
                  )}
                </div>
                
                <div className="p-4 flex-1 flex flex-col gap-2">
                  {editingId === f.firestoreId ? (
                    <div className="space-y-2">
                      <input 
                        value={editTitle} 
                        onChange={e => setEditTitle(e.target.value)}
                        className="w-full px-2 py-1.5 bg-gray-50 border border-gray-300 rounded text-xs outline-none"
                        autoFocus 
                      />
                      <div className="flex gap-1">
                        <button onClick={() => saveEdit(f.firestoreId)} className="flex-1 bg-black text-white py-1 rounded text-[9px] font-bold">OK</button>
                        <button onClick={cancelEditing} className="flex-1 bg-gray-100 text-gray-600 py-1 rounded text-[9px] font-bold">X</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="font-bold text-gray-900 text-xs line-clamp-2">{f.title}</div>
                      <div className="mt-auto flex items-center justify-between pt-2 border-t border-gray-50">
                        <div className="flex items-center gap-1">
                          <button onClick={() => startEditing(f)} className="p-1.5 text-gray-400 hover:text-black transition-colors">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                          </button>
                          <button 
                            onClick={() => toggleLanding(f)}
                            className={`p-1.5 transition-colors ${f.showOnLanding ? 'text-yellow-600' : 'text-gray-300 hover:text-yellow-500'}`}
                          >
                            <svg className="w-3.5 h-3.5" fill={f.showOnLanding ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.382-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                          </button>
                        </div>
                        <button onClick={() => handleDelete(f.firestoreId)} className="p-1.5 text-gray-300 hover:text-red-600 transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Films;
